import React from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Popover,
  Drawer,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';

const GenerateCustomPricesDialog = ({
  open,
  anchorEl,
  onClose,
  selectedTranches = [],
  priceTrancheTargets = {},
  onPriceTrancheTargetsChange,
  yieldTrancheTargets = {},
  onYieldTrancheTargetsChange,
  spreadTrancheTargets = {},
  onSpreadTrancheTargetsChange,
  ownPctTrancheTargets = {},
  onOwnPctTrancheTargetsChange,
  equityTranches = {},
  onGenerate,
  generationLoading
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const hasValue = (v) => v !== undefined && v !== null && v !== '';

  const isYieldDisabled = (t) => hasValue(spreadTrancheTargets[t]);
  const isSpreadDisabled = (t) => hasValue(yieldTrancheTargets[t]);

  const handleClearAll = () => {
    onPriceTrancheTargetsChange && onPriceTrancheTargetsChange({});
    onYieldTrancheTargetsChange && onYieldTrancheTargetsChange({});
    onSpreadTrancheTargetsChange && onSpreadTrancheTargetsChange({});
    onOwnPctTrancheTargetsChange && onOwnPctTrancheTargetsChange({});
  };

  const inputSx = (disabled) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      fontSize: isMobile ? '14px' : '13px',
      bgcolor: disabled
        ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5')
        : theme.palette.background.paper,
      '& input': { textAlign: 'center', py: isMobile ? 1 : 0.75 },
    }
  });

  const rowLabel = (label) => (
    <Typography sx={{ fontSize: isMobile ? '14px' : '12px', fontWeight: 500, color: theme.palette.text.secondary, minWidth: 60, pt: 0.5 }}>
      {label}
    </Typography>
  );

  const hasEquityTranche = selectedTranches.some(t => equityTranches[t]);

  const content = (
    <Box sx={{ p: isMobile ? 3 : 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isMobile ? 2 : 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: isMobile ? '16px' : '14px' }}>
          Custom Inputs (Optional)
        </Typography>
        <Button
          size="small"
          onClick={handleClearAll}
          sx={{ fontSize: isMobile ? '13px' : '11px', textTransform: 'none', minWidth: 'auto', px: 1, color: theme.palette.text.secondary }}
        >
          Clear
        </Button>
      </Box>

      {/* Per-tranche rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 1, mb: isMobile ? 2.5 : 2 }}>
        {/* Price */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {rowLabel('Price')}
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            {selectedTranches.map(t => (
              <Box key={t} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary, textAlign: 'center' }}>{t}</Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="e.g. 99.5"
                  type="number"
                  disabled={false}
                  value={priceTrancheTargets[t] ?? ''}
                  onChange={e => onPriceTrancheTargetsChange({ ...priceTrancheTargets, [t]: e.target.value === '' ? '' : Number(e.target.value) })}
                  sx={inputSx(false)}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Yield */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {rowLabel('Yield')}
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            {selectedTranches.map(t => (
              <TextField
                key={t} fullWidth size="small"
                placeholder="e.g. 6.5"
                type="number"
                disabled={isYieldDisabled(t)}
                value={yieldTrancheTargets[t] ?? ''}
                onChange={e => onYieldTrancheTargetsChange({ ...yieldTrancheTargets, [t]: e.target.value === '' ? '' : Number(e.target.value) })}
                sx={inputSx(isYieldDisabled(t))}
              />
            ))}
          </Box>
        </Box>

        {/* Spread */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {rowLabel('Spread')}
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            {selectedTranches.map(t => (
              <TextField
                key={t} fullWidth size="small"
                placeholder="e.g. 500"
                type="number"
                disabled={isSpreadDisabled(t)}
                value={spreadTrancheTargets[t] ?? ''}
                onChange={e => onSpreadTrancheTargetsChange({ ...spreadTrancheTargets, [t]: e.target.value === '' ? '' : Number(e.target.value) })}
                sx={inputSx(isSpreadDisabled(t))}
              />
            ))}
          </Box>
        </Box>

        {/* Own % — all columns, disabled for non-equity tranches */}
        {hasEquityTranche && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {rowLabel('Own %')}
            <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
              {selectedTranches.map(t => (
                <TextField
                  key={t} fullWidth size="small"
                  placeholder="100.00"
                  type="number"
                  disabled={!equityTranches[t]}
                  value={ownPctTrancheTargets[t] ?? ''}
                  onChange={e => onOwnPctTrancheTargetsChange({ ...ownPctTrancheTargets, [t]: e.target.value === '' ? '' : Number(e.target.value) })}
                  sx={inputSx(!equityTranches[t])}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer buttons */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          size={isMobile ? 'medium' : 'small'}
          onClick={onClose}
          sx={{ textTransform: 'none', fontSize: isMobile ? '14px' : '13px', color: theme.palette.text.secondary }}
        >
          Cancel
        </Button>
        <Button
          size={isMobile ? 'medium' : 'small'}
          variant="contained"
          color="primary"
          onClick={onGenerate}
          disabled={generationLoading}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: isMobile ? '14px' : '13px' }}
        >
          {generationLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={14} sx={{ color: 'white' }} />
              Generating...
            </Box>
          ) : 'Generate'}
        </Button>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, bgcolor: theme.palette.background.paper, maxHeight: '80vh' }
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: theme.palette.background.paper,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 10px 15px -3px rgba(0,0,0,0.5)'
            : '0 10px 15px -3px rgba(0,0,0,0.1)',
          minWidth: Math.max(240, selectedTranches.length * 100 + 80),
          mt: 1,
        }
      }}
    >
      {content}
    </Popover>
  );
};

export default GenerateCustomPricesDialog;
